import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  DialogContentText,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import InfoIcon from '@mui/icons-material/Info';
import { database } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { ref, get, remove } from 'firebase/database';
import firebaseDataService from '../../services/FirebaseDataService';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import ConfirmDialog from '../common/ConfirmDialog';
import { useTranslation } from 'react-i18next';

// Calendar Event tipi - Özel etkinlikler için
interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  createdAt: number;
  isChecked?: boolean;
  color?: string; // Özel etkinlik rengi
}

interface CalendarEventDetailModalProps {
  open: boolean;
  onClose: () => void;
  calendarEvent: CalendarEvent | null;
  onDelete?: (id: string) => void;
}

const CalendarEventDetailModal: React.FC<CalendarEventDetailModalProps> = ({
  open,
  onClose,
  calendarEvent,
  onDelete
}) => {
  const { t, i18n } = useTranslation('other');
  const dateLocale = i18n.language?.startsWith('tr') ? tr : enUS;
  const theme = useTheme();
  const [linkedEventsCount, setLinkedEventsCount] = useState<number>(0);
  const [isLoadingCount, setIsLoadingCount] = useState<boolean>(false);
  
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const loadLinkedEventsCount = async () => {
      if (!open || !calendarEvent) return;
      
      setIsLoadingCount(true);
      
      try {
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user) {
          setIsLoadingCount(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData) {
          setIsLoadingCount(false);
          return;
        }
        const companyId = userData.companyId;
        
        if (!companyId) {
          setIsLoadingCount(false);
          return;
        }
        
        const eventsRef = ref(database, `companies/${companyId}/events`);
        const eventsSnapshot = await get(eventsRef);
        
        if (!eventsSnapshot.exists()) {
          setLinkedEventsCount(0);
          setIsLoadingCount(false);
          return;
        }
        
        const eventsData = eventsSnapshot.val();
        let linkedCount = 0;
        
        Object.values(eventsData).forEach((event: any) => {
          if (event.specialEventId === calendarEvent.id) {
            linkedCount++;
          }
        });
        
        setLinkedEventsCount(linkedCount);
      } catch (error) {
        console.error('Error while counting linked events:', error);
      } finally {
        setIsLoadingCount(false);
      }
    };
    
    loadLinkedEventsCount();
  }, [open, calendarEvent]);

  const handleDeleteClick = () => {
    if (linkedEventsCount > 0) {
      setSnackbarMessage(t('events.calendarEventDetailModal.messages.cannotDeleteLinked', { count: linkedEventsCount }));
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    setConfirmDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!calendarEvent?.id) return;
    
    setIsDeleting(true);
    
    try {
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        throw new Error(t('events.messages.userLoginNotFound'));
      }
      
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        throw new Error(t('events.messages.userInfoNotFound'));
      }
      const companyId = userData.companyId;
      
      if (!companyId) {
        throw new Error(t('events.messages.companyInfoNotFound'));
      }
      
      const eventRef = ref(database, `companies/${companyId}/events/calendarEvents/${calendarEvent.id}`);
      await remove(eventRef);
      
      setSnackbarMessage(t('events.calendarEventDetailModal.messages.deleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      if (onDelete) {
        onDelete(calendarEvent.id);
      }
      
      setDeleteConfirmOpen(false);
      onClose();
    } catch (error: any) {
      console.error('Error while deleting special event:', error);
      setSnackbarMessage(error.message || t('events.calendarEventDetailModal.errors.deleteFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!calendarEvent) {
    return null;
  }

  const createdDate = new Date(calendarEvent.createdAt);
  const formattedDate = format(createdDate, 'dd MMMM yyyy HH:mm', { locale: dateLocale });
  
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            m: 0, 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: calendarEvent.color || 'primary.main',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('events.calendarEventDetailModal.title')}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom align="center" fontWeight="bold" color={calendarEvent.color}>
            {calendarEvent.title}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Chip 
              label={calendarEvent.isChecked ? t('events.calendarEventDetailModal.status.active') : t('events.calendarEventDetailModal.status.passive')} 
              color={calendarEvent.isChecked ? "success" : "error"} 
              variant="outlined"
              size="small"
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3, 
            p: 2, 
            bgcolor: 'action.hover', 
            borderRadius: 1 
          }}>
            <ColorLensIcon sx={{ mr: 1, color: calendarEvent.color }} />
            <Typography variant="body1">
              {t('events.calendarEventDetailModal.fields.eventColor')}
            </Typography>
            <Box sx={{ 
              ml: 'auto', 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              bgcolor: calendarEvent.color || '#1976d2',
              border: '1px solid rgba(0,0,0,0.1)'
            }} />
          </Box>
          
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('events.calendarEventDetailModal.fields.description')}
          </Typography>
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body1">
              {calendarEvent.description || <i>{t('events.calendarEventDetailModal.fields.noDescription')}</i>}
            </Typography>
          </Paper>
          
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('events.calendarEventDetailModal.fields.linkedEvents')}
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: linkedEventsCount > 0 ? 'info.light' : 'grey.100', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <CalendarMonthIcon sx={{ mr: 1 }} />
            {isLoadingCount ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body1">
                  {t('events.calendarEventDetailModal.fields.countingLinked')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1">
                {t('events.calendarEventDetailModal.fields.linkedCount', { count: linkedEventsCount })}
              </Typography>
            )}
          </Paper>
          
          <Typography variant="subtitle2" color="text.secondary" align="right">
            {t('events.calendarEventDetailModal.fields.createdAt', { date: formattedDate })}
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
          >
            {t('events.calendarEventDetailModal.actions.close')}
          </Button>
          
          <Tooltip 
            title={linkedEventsCount > 0 
              ? t('events.calendarEventDetailModal.messages.cannotDeleteLinkedTooltip', { count: linkedEventsCount })
              : t('events.calendarEventDetailModal.actions.deleteSpecialEvent')}
          >
            <span>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                disabled={linkedEventsCount > 0}
              >
                {t('events.calendarEventDetailModal.actions.delete')}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('events.calendarEventDetailModal.deleteDialog.title')}
        content={t('events.calendarEventDetailModal.deleteDialog.content', { title: calendarEvent.title })}
        confirmButtonText={t('events.calendarEventDetailModal.actions.delete')}
        cancelButtonText={t('events.calendarEventDetailModal.actions.cancel')}
      />
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CalendarEventDetailModal; 